import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Award, ChevronLeft, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import SEO, { SITE_URL, breadcrumbSchema } from "@/components/seo/SEO";

function formatDate(iso) {
    try {
        return new Date(iso).toLocaleDateString("es-ES", {
            day: "2-digit",
            month: "long",
            year: "numeric",
        });
    } catch {
        return "";
    }
}

const toAbsolute = (src) =>
    src && src.startsWith("/") ? `${SITE_URL}${src}` : src;

/* ---------- Galería con navegación ---------- */
const Gallery = ({ images }) => {
    const [idx, setIdx] = useState(0);
    if (!images || images.length === 0) return null;
    const prev = () => setIdx((i) => (i - 1 + images.length) % images.length);
    const next = () => setIdx((i) => (i + 1) % images.length);
    return (
        <div data-testid="news-gallery" className="mt-12">
            <div className="relative aspect-[16/9] overflow-hidden border border-[#E2E4E9] bg-[#F8F9FA]">
                <img
                    src={images[idx]}
                    alt={`Fotografía ${idx + 1} de ${images.length} de la noticia`}
                    loading="lazy"
                    className="w-full h-full object-cover"
                />
                {images.length > 1 && (
                    <>
                        <button
                            type="button"
                            onClick={prev}
                            data-testid="btn-gallery-prev"
                            aria-label="Foto anterior"
                            className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 flex items-center justify-center bg-white/90 hover:bg-white text-[#082050] border border-[#E2E4E9] transition-colors"
                        >
                            <ChevronLeft size={18} />
                        </button>
                        <button
                            type="button"
                            onClick={next}
                            data-testid="btn-gallery-next"
                            aria-label="Foto siguiente"
                            className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 flex items-center justify-center bg-white/90 hover:bg-white text-[#082050] border border-[#E2E4E9] transition-colors"
                        >
                            <ChevronRight size={18} />
                        </button>
                    </>
                )}
            </div>
            {images.length > 1 && (
                <div className="mt-3 flex items-center justify-center gap-2">
                    {images.map((_, i) => (
                        <button
                            key={i}
                            type="button"
                            onClick={() => setIdx(i)}
                            data-testid={`gallery-dot-${i}`}
                            aria-label={`Ir a la foto ${i + 1}`}
                            className={`h-1.5 w-6 transition-colors ${
                                i === idx ? "bg-[#082050]" : "bg-[#E2E4E9] hover:bg-[#082050]/40"
                            }`}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

/* ---------- Foto inline con pie de foto ---------- */
const Figure = ({ image, testid }) => (
    <figure data-testid={testid} className="mt-10">
        <div className="overflow-hidden border border-[#E2E4E9] bg-[#F8F9FA]">
            <img
                src={image.url}
                alt={image.caption || "Fotografía de la noticia"}
                loading="lazy"
                className="w-full h-auto"
            />
        </div>
        {image.caption && (
            <figcaption className="mt-2 text-xs sm:text-sm text-[#6B7280] leading-relaxed">
                {image.caption}
            </figcaption>
        )}
    </figure>
);

export default function NewsDetail() {
    const { slug } = useParams();
    const [item, setItem] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/news/${slug}`);
                setItem(data);
            } catch (e) {
                setError("Noticia no encontrada");
            } finally {
                setLoading(false);
            }
        })();
    }, [slug]);

    if (loading) {
        return (
            <div data-testid="news-detail-loading" className="max-w-3xl mx-auto px-4 py-24 text-[#4A5568]">
                Cargando...
            </div>
        );
    }
    if (error || !item) {
        return (
            <div data-testid="news-detail-error" className="max-w-3xl mx-auto px-4 py-24">
                <div className="text-[#4A5568]">{error || "Noticia no encontrada"}</div>
                <button
                    onClick={() => navigate("/noticias")}
                    className="mt-6 text-[#082050] underline"
                    data-testid="btn-back-news"
                >
                    Volver a noticias
                </button>
            </div>
        );
    }

    return (
        <article data-testid="news-detail" className="bg-white">
            <SEO
                title={item.seo_title || item.title}
                description={item.seo_description || item.excerpt}
                path={`/noticias/${item.slug}`}
                image={toAbsolute(item.cover_image)}
                type="article"
                jsonLd={[
                    {
                        "@context": "https://schema.org",
                        "@type": "NewsArticle",
                        "headline": item.title,
                        "description": item.excerpt,
                        "image": [
                            item.cover_image,
                            ...(item.gallery || []),
                        ].filter(Boolean).map(toAbsolute),
                        "datePublished": item.date || item.created_at,
                        "author": { "@type": "Organization", "name": "ASATTA" },
                        "publisher": {
                            "@type": "Organization",
                            "name": "ASATTA",
                            "logo": {
                                "@type": "ImageObject",
                                "url": "https://asatta.org/logo512.png",
                            },
                        },
                    },
                    breadcrumbSchema([
                        { name: "Inicio", path: "/" },
                        { name: "Noticias", path: "/noticias" },
                        { name: item.title, path: `/noticias/${item.slug}` },
                    ]),
                ]}
            />
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-24 pb-16">
                <Link
                    to="/noticias"
                    className="inline-flex items-center gap-2 text-sm text-[#082050]/70 hover:text-[#082050]"
                    data-testid="btn-back-news"
                >
                    <ArrowLeft size={14} />
                    Volver a noticias
                </Link>
                <div className="mt-8 flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-widest">
                    {item.featured && (
                        <span className="inline-flex items-center gap-1.5 bg-[#C9A227]/15 text-[#8A6D1A] border border-[#C9A227]/40 px-2.5 py-1 font-semibold">
                            <Award size={12} />
                            Hito ASATTA
                        </span>
                    )}
                    {item.category && (
                        <span className="text-[#082050]/70 font-medium border border-[#082050]/20 px-2.5 py-1">
                            {item.category}
                        </span>
                    )}
                    <span className="text-[#082050]/60 px-1 py-1">
                        {formatDate(item.date || item.created_at)}
                    </span>
                </div>
                <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#082050] tracking-tight leading-tight">
                    {item.title}
                </h1>
                <p className="mt-4 text-lg text-[#4A5568] text-justify">{item.excerpt}</p>
                {item.cover_image && (
                    <div className="mt-10 aspect-[16/9] overflow-hidden border border-[#E2E4E9]">
                        <img
                            src={item.cover_image}
                            alt={`Imagen destacada de la noticia: ${item.title}`}
                            loading="lazy"
                            className="w-full h-full object-cover"
                        />
                    </div>
                )}
                <div className="mt-10 prose prose-lg max-w-none text-[#4A5568] whitespace-pre-line leading-relaxed text-justify">
                    {item.content}
                </div>
                {item.intro_image && (
                    <Figure image={item.intro_image} testid="news-intro-image" />
                )}
                <Gallery images={item.gallery} />
                {item.sections && item.sections.length > 0 && (
                    <div className="mt-14 space-y-14">
                        {item.sections.map((sec, i) => {
                            const imgs = sec.images || [];
                            const imgsAt = (pos) =>
                                imgs
                                    .map((im, k) => ({ im, k }))
                                    .filter(({ im }) => (im.position ?? 0) === pos);
                            const startImgs = imgsAt(0);
                            const endImgs = imgs
                                .map((im, k) => ({ im, k }))
                                .filter(({ im }) => (im.position ?? 0) > sec.paragraphs.length);
                            return (
                                <section key={i} data-testid={`news-section-${i}`}>
                                    {sec.heading && (
                                        <h2 className="text-2xl sm:text-3xl font-semibold text-[#082050] tracking-tight mb-5">
                                            {sec.heading}
                                        </h2>
                                    )}
                                    {startImgs.map(({ im, k }) => (
                                        <Figure
                                            key={`s${i}-start-${k}`}
                                            image={im}
                                            testid={`news-section-${i}-image-0-${k}`}
                                        />
                                    ))}
                                    <div className="space-y-5">
                                        {sec.paragraphs.map((p, j) => (
                                            <React.Fragment key={j}>
                                                <p className="text-base sm:text-lg text-[#4A5568] leading-relaxed text-justify">
                                                    {p}
                                                </p>
                                                {imgsAt(j + 1).map(({ im, k }) => (
                                                    <Figure
                                                        key={`s${i}-p${j}-${k}`}
                                                        image={im}
                                                        testid={`news-section-${i}-image-${j + 1}-${k}`}
                                                    />
                                                ))}
                                            </React.Fragment>
                                        ))}
                                    </div>
                                    {endImgs.map(({ im, k }) => (
                                        <Figure
                                            key={`s${i}-end-${k}`}
                                            image={im}
                                            testid={`news-section-${i}-image-end-${k}`}
                                        />
                                    ))}
                                </section>
                            );
                        })}
                    </div>
                )}
                {item.press_contact && (
                    <div
                        data-testid="news-press-contact"
                        className="mt-14 bg-[#082050] text-white p-6 sm:p-8"
                    >
                        <div className="text-[10px] uppercase tracking-[0.22em] text-white/60">
                            Contacto de prensa
                        </div>
                        <p className="mt-3 text-sm sm:text-base whitespace-pre-line leading-relaxed text-white/90">
                            {item.press_contact}
                        </p>
                    </div>
                )}
                {item.cta_url && item.cta_label && (
                    <div className="mt-12 border-2 border-[#C9A227] bg-[#C9A227]/5 p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div className="text-sm text-[#4A5568]">
                            <span className="block text-[10px] uppercase tracking-widest text-[#8A6D1A] font-semibold mb-1">
                                {item.cta_url.startsWith("/directorio/")
                                    ? "Asociado ASATTA"
                                    : "ASATTA"}
                            </span>
                            {item.cta_url.startsWith("/directorio/")
                                ? "Descubre su ficha completa en nuestro directorio de alojamientos."
                                : "Sigue explorando en nuestra web."}
                        </div>
                        {item.cta_url.startsWith("/") ? (
                            <Link
                                to={item.cta_url}
                                data-testid="btn-news-cta"
                                className="inline-flex items-center gap-2 bg-[#082050] text-white px-6 py-3.5 text-sm font-medium hover:bg-[#082050]/90 shrink-0 transition-transform hover:-translate-y-0.5"
                            >
                                {item.cta_label}
                                <ArrowRight size={14} />
                            </Link>
                        ) : (
                            <a
                                href={item.cta_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="btn-news-cta"
                                className="inline-flex items-center gap-2 bg-[#082050] text-white px-6 py-3.5 text-sm font-medium hover:bg-[#082050]/90 shrink-0 transition-transform hover:-translate-y-0.5"
                            >
                                {item.cta_label}
                                <ArrowRight size={14} />
                            </a>
                        )}
                    </div>
                )}
            </div>
        </article>
    );
}
